<template>
  <div>
    <!-- Trial banner — sticky top, dismissible per-session only, reappears on reload. -->
    <div
      v-if="state === 'trial' && !snoozed"
      class="pb-banner pb-banner--trial"
      role="status"
      aria-live="polite"
    >
      <span class="pb-banner__msg">
        <strong>{{ daysLeft }} {{ daysLeft === 1 ? 'day' : 'days' }} left</strong>
        on your free trial. Add a card now to keep your team's access.
      </span>
      <button type="button" class="pb-banner__cta" @click="openAddCard">Add card</button>
      <button
        type="button"
        class="pb-banner__dismiss"
        aria-label="Dismiss banner for this session"
        @click="snoozed = true"
      >&times;</button>
    </div>

    <!-- Demo banner — lives above the dashboard for sandbox users so they
         always see "this is a demo, start a trial to keep your work". -->
    <div
      v-if="state === 'demo' && !snoozed"
      class="pb-banner pb-banner--demo"
      role="status"
      aria-live="polite"
    >
      <span class="pb-banner__msg">
        <strong>You're in a demo.</strong>
        Nothing you do here saves. Start a trial to unlock your real account.
      </span>
      <router-link :to="{ path: '/pricing', query: { tab: 'company' } }" class="pb-banner__cta">Start a trial</router-link>
      <button
        type="button"
        class="pb-banner__dismiss"
        aria-label="Dismiss banner for this session"
        @click="snoozed = true"
      >&times;</button>
    </div>

    <!-- Hard modal — trial expired, blocks access until a card is on file. -->
    <div
      v-if="state === 'hold'"
      class="pb-hold"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="pb-hold-title"
    >
      <div class="pb-hold__card">
        <h2 id="pb-hold-title" class="pb-hold__title">Your trial has ended</h2>
        <p class="pb-hold__msg">Add a payment method to restore your team's access. You can cancel any time from your account.</p>
        <button type="button" class="pb-hold__cta" @click="openAddCard">Add payment method</button>
        <p class="pb-hold__meta">Need help? <a href="mailto:support@homeoftraining.com">support@homeoftraining.com</a></p>
      </div>
    </div>

    <add-card-modal
      v-if="showAddCard"
      @close="showAddCard = false"
      @saved="onCardSaved"
    />
  </div>
</template>

<script>
import axios from 'axios';
import AddCardModal from './AddCardModal.vue';

const API_BASE = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000/api';

export default {
  name: 'BillingBanner',
  components: { AddCardModal },
  data() {
    return {
      state: null, // null | 'ok' | 'trial' | 'hold'
      daysLeft: 0,
      hasCard: false,
      snoozed: false,
      showAddCard: false,
      loaded: false,
    };
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      try {
        const token = localStorage.getItem('hot-token');
        if (!token) return;
        const { data } = await axios.get(`${API_BASE}/new-checkout/billing-status`, {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: () => true,
        });
        if (!data || typeof data !== 'object') return;
        this.state = data.state || 'ok';
        this.daysLeft = Number(data.days_left || 0);
        this.hasCard = !!data.has_card;
        this.loaded = true;
      } catch (e) {
        // Silent — banner just won't show. The dashboard should never break
        // because of an optional billing widget.
      }
    },
    openAddCard() {
      this.showAddCard = true;
    },
    onCardSaved(newStatus) {
      this.showAddCard = false;
      if (newStatus && typeof newStatus === 'object') {
        this.state = newStatus.state || 'ok';
        this.daysLeft = Number(newStatus.days_left || 0);
        this.hasCard = !!newStatus.has_card;
      } else {
        this.load();
      }
    },
  },
};
</script>

<style scoped>
.pb-banner {
  position: sticky;
  top: 0;
  z-index: 1030;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 18px;
  font: 500 14px/1.35 Inter, system-ui, sans-serif;
  color: #fff;
  background: linear-gradient(90deg, #fe672c, #d84f16);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}
.pb-banner--demo {
  background: linear-gradient(90deg, #111827, #1f2937);
}
.pb-banner--demo .pb-banner__cta {
  background: #fe672c;
  color: #fff;
  text-decoration: none;
}
.pb-banner--demo .pb-banner__cta:hover { background: #e55617; }
.pb-banner__msg { flex: 1; min-width: 0; }
.pb-banner__msg strong { font-weight: 700; }
.pb-banner__cta {
  appearance: none;
  border: 0;
  padding: 7px 14px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.3px;
  color: #1a1a1a;
  background: #fff;
  cursor: pointer;
}
.pb-banner__cta:hover { background: #f6f6f6; }
.pb-banner__dismiss {
  appearance: none;
  background: transparent;
  border: 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}
.pb-banner__dismiss:hover { color: #fff; }
.pb-hold {
  position: fixed;
  inset: 0;
  z-index: 1080;
  background: rgba(10, 10, 10, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.pb-hold__card {
  background: #fff;
  border-radius: 16px;
  max-width: 460px;
  width: 100%;
  padding: 30px;
  text-align: center;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.4);
}
.pb-hold__title {
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 12px;
  color: #1a1a1a;
}
.pb-hold__msg {
  font-size: 14.5px;
  color: #444;
  margin: 0 0 20px;
  line-height: 1.5;
}
.pb-hold__cta {
  appearance: none;
  border: 0;
  background: #fe672c;
  color: #fff;
  font-weight: 700;
  font-size: 15px;
  padding: 13px 22px;
  border-radius: 10px;
  cursor: pointer;
  width: 100%;
}
.pb-hold__cta:hover { background: #e55617; }
.pb-hold__meta {
  font-size: 12.5px;
  color: #888;
  margin: 16px 0 0;
}
.pb-hold__meta a { color: #fe672c; text-decoration: none; }
</style>
